interface Tag {
    id: string;
    [key: string]: any;
}

interface Comment {
    id: string;
    commentable_id?: string;
    commentable_type?: string;
    Tag?: Tag;
    replies?: Comment[];
    [key: string]: any;
}

interface CommentTypeResult {
    isTagComment: boolean;
    tagId: string | null;
}

/**
 * Helper function to determine if a comment is part of a tag comment thread
 * @param parentId - The ID of the parent comment
 * @param comments - Array of comments to search through
 * @returns Object containing isTagComment, tagId
 */
export const determineCommentType = (parentId: string, comments: Comment[]): CommentTypeResult => {
    // Search for the parent comment in the nested structure
    const findCommentInNestedStructure = (comments: Comment[], targetId: string): Comment | null => {
        for (const comment of comments) {
            if (comment.id === targetId) {
                return comment;
            }
            // Search in replies recursively
            if (comment.replies && comment.replies.length > 0) {
                const foundInReplies = findCommentInNestedStructure(comment.replies, targetId);
                if (foundInReplies) {
                    return foundInReplies;
                }
            }
        }
        return null;
    };

    const parentComment = findCommentInNestedStructure(comments, parentId);

    // Check if this comment is part of a tag comment thread
    let isTagComment = false;
    let tagId: string | null = null;

    if (parentComment) {
        // Check if the parent comment has a Tag property directly
        if (parentComment.Tag) {
            isTagComment = true;
            tagId = parentComment.Tag.id;
        } else if (parentComment.commentable_type === "Taggable") {
            // This is a tag comment, find the root comment with the same commentable_id
            const findRootCommentByCommentableId = (comments: Comment[], commentableId: string): Comment | null => {
                for (const comment of comments) {
                    if (comment.commentable_id === commentableId && comment.Tag) {
                        return comment;
                    }
                    if (comment.replies && comment.replies.length > 0) {
                        const foundInReplies = findRootCommentByCommentableId(comment.replies, commentableId);
                        if (foundInReplies) {
                            return foundInReplies;
                        }
                    }
                }
                return null;
            };

            const rootComment = findRootCommentByCommentableId(comments, parentComment.commentable_id!);
            if (rootComment) {
                isTagComment = true;
                tagId = rootComment.Tag!.id;
            }
        }
    }

    return {
        isTagComment,
        tagId,
    };
};
